/**
 * @author
 * David Rivera (jherax)
 * https://github.com/jherax
 */

import addStyleToHead from '../utils/dom/addStyleToHead';
import fetchJSON from '../utils/fetch/fetchJSON';
import extendApplicant from './extend-applicant';
import getStyle from './get-custom-style';

/**
 * @private
 * Builds the object with the data to pass to `fetchJSON`
 *
 * @param {Object} payload: data to send to `fetchJSON`
 * @param {Object} applicant: object to extend in the payload
 * @param {Function} payloadReducer: Function to alter the final payload sent to API
 * @returns {Object}
 */
function getPayloadData(payload, applicant, payloadReducer) {
  const payloadObj = Object.assign(payload, { Applicant: applicant });
  if (payloadReducer) {
    return {data: payloadReducer(payloadObj)};
  }
  return {data: payloadObj};
}

/**
 * @private
 * Builds the HTML wrapper for each plan in the list of plans
 * when calling `PrepareWidget.buildHTMLBlock`
 *
 * @param {Object} plan: the plan data
 * @throws {TypeError} if is not implemented.
 */
function buildHTMLPlan(plan) { // eslint-disable-line
  throw new TypeError('Not implemented -> buildHTMLPlan(plan) : string');
}

/**
 * @private
 * Validates if the element with the id provided exists and returns it.
 *
 * @param {String} id: element id where the HTML will be inserted
 * @param {String} widgetName: name of the widget which is requesting to find the div container
 * @throws {Error} if the element with the `id` does not exist.
 * @returns {Element}
 */
function getContainer(id, widgetName) {
  const div = document.getElementById(id);
  if (!id || !div) throw new Error(`${widgetName} did not find an element matching the ID ${id}`);
  return div;
}

/**
 * @public
 * Initializes common procedures for the widget.
 *
 * @class PrepareWidget
 */
export default class PrepareWidget {
  /**
   * Creates an instance of PrepareWidget.
   * @memberof PrepareWidget
   */
  constructor({containerClass = '', widgetClass = '', widgetName = ''}) {
    this.widgetName = widgetName;
    this.widgetClass = widgetClass;
    this.containerClass = containerClass;

    /** @override */
    this.buildHTMLPlan = buildHTMLPlan;
  }

  /**
   * Sets widget title and disclaimer text
   *
   * @param {Object} options: An Object which contains the following options:
   *        - {String} title: text that will be heading plans widget
   *        - {String} disclaimer: disclaimer text, placed at the bottom
   *        - {String} divId: element id where the HTML will be inserted
   * @returns {Element}
   * @memberof PrepareWidget
   */
  configure(options) {
    const { title = '', disclaimer = '', css } = options,
      div = getContainer(options.divId, this.widgetName),
      styleId = `${this.containerClass}-custom`;
    // Append custom style to HEAD according to div's width
    if (!document.getElementById(this.containerClass)) {
      addStyleToHead(css, this.containerClass);
    }
    if (!document.getElementById(styleId)) {
      addStyleToHead(getStyle(div.clientWidth, this.containerClass, this.widgetClass), styleId);
    }
    this.disclaimer = disclaimer;
    this.title = title;
    return div;
  }

  /**
   * Sets the payload object to be sent to the endpoint service.
   *
   * @param {Object} payload: data to send to the endpoint
   * @memberof PrepareWidget
   */
  setPayload(payload) {
    this.payload = payload;
  }

  /**
   * Get plans from the given URL.
   *
   * @param {String} url: the url of the endpoint
   * @param {Number} howMany: How many plans do you want to get
   * @param {String} zipCode: override the zipCode from hcStorage
   * @param {Function} payloadReducer: Function to alter the final payload sent to API
   * @returns {Promise}
   * @memberof PrepareWidget
   */
  getPlans(url, howMany = 1, zipCode = '', payloadReducer) {
    return extendApplicant.setGeoLocation(this.payload.Applicant, zipCode)
      .then(applicant => extendApplicant.setSizePage(applicant, howMany))
      .then(applicant => fetchJSON(url, getPayloadData(this.payload, applicant, payloadReducer)));
  }

  /**
   * Builds the HTML block to render the plans.
   *
   * @param {Array} plans: the list of plans
   * @returns {String}
   * @memberof PrepareWidget
   */
  buildHTMLBlock(plans = []) {
    return ([
      `<div class="${this.containerClass}">`,
      this.title ? `<div class="${this.containerClass}__title">${this.title}</div>` : '',
      `<div class="${this.containerClass}__list">`,
    ]).concat(plans.map(this.buildHTMLPlan))
      .concat([
        '</div>',
        this.disclaimer ? `<div class="${this.containerClass}__disclaimer">${this.disclaimer}</div>` : '',
        '</div>',
      ])
      .join('');
  }
}
